import { cn } from "@/shared/utils";
import { useId } from "react";
import { Helptext } from "./helptext";
import { Label } from "./label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

type SelectOption = {
    label: string;
    value: string;
};

type SelectfieldProps<T extends string> = {
    label: string;
    value: T;
    onValueChange: (value: T) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    optional?: boolean;
    helptext?: string;
    className?: string;
    name?: string;
    readonly?: boolean;
    ["data-testid"]?: string;
};

// simple selectfield, useful if a RHF formfield is not necessary
export function Selectfield<T extends string>({
    label,
    value,
    onValueChange,
    options,
    placeholder,
    disabled = false,
    optional = false,
    helptext,
    className,
    name,
    readonly,
    ...props
}: SelectfieldProps<T>) {
    const id = useId();
    return (
        <div className={cn("grid gap-1", className)}>
            <Label htmlFor={id} optional={optional}>
                {label}
            </Label>
            {!readonly && (
                <Select value={value} onValueChange={onValueChange} disabled={disabled} name={name} form="force-native-select">
                    <SelectTrigger id={id} className="w-full" {...props}>
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            {readonly && <p className="text-content-subtle">{value}</p>}
            {helptext && <Helptext>{helptext}</Helptext>}
        </div>
    );
}
